import React, { useState, useEffect, useMemo } from 'react';
import { ReactComponent as HeartIcon } from '../icons/HeartIcon.svg';
import { ReactComponent as HeartOutlineIcon } from '../icons/HeartOutlineIcon.svg';
import { ReactComponent as LocationIcon } from '../icons/locationIcon.svg';
import { ReactComponent as PlanIcon } from '../icons/PlanIcon.svg';
import { ReactComponent as RequirementsIcon } from '../icons/RequirementsIcon.svg';
import { ReactComponent as StarIcon } from '../icons/StarIcon.svg';
import { ResourceSchema, ResourceType } from '../data/types';
import { capitalizeString, getDirectLinkFromGoogleDriveSharedLink, styleThousandCommas, planRecurrenceMap } from '../utils/helpers';

type PartnerResourceCardProps = {
  resource: ResourceSchema;
  selectedCategory: ResourceType;
  categoryIcon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  isSaved: boolean;
  onCardClick: (resource: ResourceSchema) => void;
  onSaveResource: (resource: ResourceSchema) => void;
  onRemoveSavedResource: (resource: ResourceSchema) => void;
};

const PartnerResourceCard: React.FC<PartnerResourceCardProps> = ({
  resource,
  selectedCategory,
  categoryIcon: CategoryIcon,
  isSaved,
  onCardClick,
  onSaveResource,
  onRemoveSavedResource,
}) => {
  const logoUrl = resource.logo ? getDirectLinkFromGoogleDriveSharedLink(resource.logo) : null;
  // console.log("resource", resource)
  // Process photos: prioritize bgPhotos array, fallback to bgPhoto for backwards compatibility
  const bgPhotoUrls = useMemo(() => {
    const photos: string[] = [];
    
    // Add bgPhotos array (up to 5 photos)
    if (resource.bgPhotos && resource.bgPhotos.length > 0) {
      photos.push(...resource.bgPhotos.slice(0, 5).map(photo => 
        getDirectLinkFromGoogleDriveSharedLink(photo)
      ));
    }
    
    // Add bgPhoto for backwards compatibility (only if not already in array)
    if (resource.bgPhoto) {
      const bgPhotoUrl = getDirectLinkFromGoogleDriveSharedLink(resource.bgPhoto);
      if (!photos.includes(bgPhotoUrl)) {
        photos.push(bgPhotoUrl);
      }
    }
    
    return photos;
  }, [resource.bgPhotos, resource.bgPhoto]);
  
  // Memoize photos key for useEffect dependency
  const photosKey = useMemo(() => {
    return resource.bgPhotos?.join(',') || '';
  }, [resource.bgPhotos]);
  
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [imageError, setImageError] = useState(false);
  
  // Reset photo index when resource photos change
  useEffect(() => {
    setCurrentPhotoIndex(0);
    setImageError(false);
  }, [photosKey, resource.bgPhoto]);
  
  // Ensure current index is within bounds
  const safePhotoIndex = Math.min(currentPhotoIndex, Math.max(0, bgPhotoUrls.length - 1));
  const currentPhotoUrl = bgPhotoUrls.length > 0 ? bgPhotoUrls[safePhotoIndex] : null;
  
  // Get minimum price plan and its recurrence
  const minPricePlan = useMemo(() => {
    if (!resource.plans || resource.plans.length === 0) return null;
    return resource.plans.reduce((min: any, plan: any) => {
      const minRate = min?.plan_rate || 0;
      const planRate = plan?.plan_rate || 0;
      return planRate > 0 && (minRate === 0 || planRate < minRate) ? plan : min;
    }, null);
  }, [resource.plans]);

  const minPrice = minPricePlan?.plan_rate || 0;
  const minPriceRecurrence = minPricePlan?.plan_recurrence 
    ? planRecurrenceMap[minPricePlan.plan_recurrence as keyof typeof planRecurrenceMap] || 'hr'
    : 'hr';
  
  const planCount = resource.plans?.length || 0;
  const requirementsCount = resource.requirements?.length || 0;
  // Estimate inventory count from resource inventory if available
  var inventoryCount = 0;
  if (resource?.resource_type && ["training", "expert"].includes(resource?.resource_type as string)) {
    inventoryCount = resource.resource_inventory?.['schedule']?.length || 0;
  } else if (resource?.resource_type && ["kitchen", "farm"].includes(resource?.resource_type as string)) {
    inventoryCount = resource.resource_inventory?.['items']?.length || 0;
  }

  return (
    <div
      className="bg-white border border-[#F5F5F5] p-2 rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
      onClick={() => onCardClick(resource)}
    >
      {/* Image Section */}
      <div className="relative w-full h-48 bg-gray-200 rounded-xl overflow-hidden">
        {currentPhotoUrl && !imageError ? (
          <img
            src={currentPhotoUrl}
            alt={resource.name}
            className="w-full h-full object-cover rounded-xl transition-opacity duration-300"
            onError={() => setImageError(true)}
            onLoad={() => setImageError(false)}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-gray-300 via-gray-200 to-gray-400 rounded-xl flex items-center justify-center">
            <div className="text-center p-4">
              <CategoryIcon className="w-16 h-16 text-gray-400 mx-auto mb-2 opacity-60" />
              <p className="text-xs text-gray-500 font-raleway font-medium">
                {resource.name || 'Resource'}
              </p>
            </div>
          </div>
        )}
        
        {/* Tag */}
        <div className="absolute top-3 left-3">
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium ${
              resource.active
                ? 'bg-[#E7F5EC] text-[#0F973D]'
                : 'bg-[#E3F2FD] text-[#1976D2]'
            }`}
          >
            {resource.active ? 'Available' : 'Featured'}
          </span>
        </div>

        {/* Heart Icon */}
        <div
          className="absolute top-3 right-3"
          onClick={(e) => {
            e.stopPropagation();
            if (isSaved) {
              onRemoveSavedResource(resource);
            } else {
              onSaveResource(resource);
            }
          }}
        >
          {isSaved ? (
            <HeartIcon className="w-6 h-6 text-primaryDark-400" />
          ) : (
            <HeartOutlineIcon className="w-6 h-6 text-white" />
          )}
        </div>

        {/* Image Navigation Dots */}
        {bgPhotoUrls.length > 1 && (
          <div 
            className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            {bgPhotoUrls.slice(0, 5).map((_, index) => (
              <div
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentPhotoIndex(index);
                  setImageError(false);
                }}
                className={`w-2 h-2 rounded-full cursor-pointer ${
                  index === safePhotoIndex ? 'bg-white' : 'bg-white/50'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="p-4 bg-[#FAFAFA] h-auto min-h-[190px] mt-1 rounded-lg">
        {/* Logo and Name */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-14 h-14 rounded-lg border border-[#E9E9E9] flex items-center justify-center bg-inherit flex-shrink-0">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={resource.name}
                className="w-10 h-10 object-contain"
              />
            ) : (
              <CategoryIcon className="w-6 h-6 text-primaryDark-400" />
            )}
          </div>
          <div className="flex flex-col flex-1 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <h3 className="font-raleway font-bold text-lg text-[#1A1A1A] truncate flex-1 min-w-0">
                {resource.name || 'Resource'}
              </h3>
              {resource.rating ? (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <StarIcon className="w-4 h-4 text-[#ED8A19]" />
                  <span className="text-sm font-raleway font-semibold text-[#1A1A1A] whitespace-nowrap">
                    {resource.rating.toFixed(1)}
                  </span>
                </div>
              ) : (<></>)}
            </div>
            {/* Description */}
            <p className="text-sm text-[#808080] font-open-sans line-clamp-2 mt-1">
              {resource.description || ''}
            </p>
          </div>
        </div>

        {/* Details with Icons */}
        <div className="space-y-2 mb-4">
          {/* Location */}
          {resource.address ? (
            <div className="flex items-center gap-2 text-sm">
              <LocationIcon className="w-4 h-4 text-primaryDark-400 flex-shrink-0" />
              <span className="text-black font-raleway font-medium truncate min-w-0">
                {resource.address}
              </span>
            </div>
          ) : (<></>)}

          {/* Booking Plans */}
          {/* {planCount > 0 ? (
            <div className="flex items-center gap-2 text-sm">
              <PlanIcon className="w-4 h-4 text-primaryDark-400 flex-shrink-0" />
              <span className="text-black font-open-sans">
                {planCount} Booking Plans
              </span>
            </div>
          ) : (<></>)} */}

          {/* Kitchen Items */}
          {(inventoryCount > 0 || ["training", "expert"].includes(resource.resource_type as string)) ? (<div className="flex items-center gap-2 text-sm">
            <svg
              className="w-4 h-4 text-primaryDark-400 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M8.1 13.34l2.83-2.83L3.91 3.5c-1.56 1.56-1.56 4.09 0 5.65l4.19 4.19zm6.78-1.81c1.53.71 3.68.21 5.27-1.38 1.91-1.91 2.28-4.65.81-6.12-1.46-1.46-4.20-1.10-6.12.81-1.59 1.59-2.09 3.74-1.38 5.27L3.7 19.87l1.41 1.41L12 14.41l6.88 6.88 1.41-1.41L13.41 13l1.47-1.47z" />
            </svg>
            <span className="text-black font-raleway font-medium">
              {inventoryCount} {["training", "expert"].includes(resource.resource_type as string) ? (inventoryCount > 1 ? `${capitalizeString(selectedCategory)} Schedules` : `${capitalizeString(selectedCategory)} Schedule`) : (inventoryCount > 1 ? "Inventory Items" : "Inventory Item")} 
            </span>
          </div>) : (<></>)}

          {/* Rates /Booking Plans */}
          {
            (planCount > 0 || (["kitchen"].includes(resource.resource_type as string) && planCount > 0)) ? (
              <div className="flex items-center gap-2 text-sm">
                <PlanIcon className="w-4 h-4 text-primaryDark-400 flex-shrink-0" />
                <span className="text-black font-raleway font-medium">
                  {planCount} Booking Plans
                </span>
              </div>
            ) : (
                (resource?.total_amount) ? (
                  <div className="flex items-center gap-2 text-sm">
                    <PlanIcon className="w-4 h-4 text-primaryDark-400 flex-shrink-0" />
                    <span className="text-black font-raleway font-medium">
                      ${styleThousandCommas(resource.total_amount.toFixed(2))}
                    </span>
                  </div>
                ) : (
                  (["training", "expert"].includes(resource.resource_type as string) && resource?.active) ? (
                    <div className="flex items-center gap-2 text-sm">
                      <PlanIcon className="w-4 h-4 text-primaryDark-400 flex-shrink-0" />
                      <span className="text-black font-raleway font-medium">
                        Free
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm">
                      <PlanIcon className="w-4 h-4 text-primaryDark-400 flex-shrink-0" />
                      <span className="text-black font-raleway font-medium">
                        No {["kitchen"].includes(resource.resource_type as string) ? "booking plan" : "rate"} added
                      </span>
                    </div>
                  )
                )
            )
          }

          {/* Requirements */}
          {(requirementsCount > 0 || (["kitchen", "funding"].includes(resource.resource_type as string) && requirementsCount > 0)) ? (<div className="flex items-center gap-2 text-sm">
            <RequirementsIcon className="w-4 h-4 text-primaryDark-400 flex-shrink-0" />
            <span className="text-black font-raleway font-medium">
              {requirementsCount} Requirements
            </span>
          </div>) : (
            <div className="flex items-center gap-2 text-sm">
              <RequirementsIcon className="w-4 h-4 text-primaryDark-400 flex-shrink-0" />
              <span className="text-black font-raleway font-medium">
                No requirement added
              </span>
            </div>
          )}
        </div>

        {/* Price */}
        {minPrice > 0 ? (
          <div className="mt-4 pt-4 border-t border-[#E9E9E9]">
            <p className="text-sm text-[#1A1A1A] font-open-sans">
              From <span className="font-bold">${styleThousandCommas(minPrice.toFixed(2))}/{minPriceRecurrence}</span>
            </p>
          </div>
        ) : (<></>)}
      </div>
    </div>
  );
};

export default PartnerResourceCard;

